import { useEffect, useMemo, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Banner,
  BlockStack,
  Text,
  InlineStack,
  Checkbox,
  Badge,
  Button,
} from '@shopify/polaris';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import { useStore } from '../../providers/StoreProvider';
import {
  CampaignScheduleFields,
  defaultScheduleValue,
  scheduleFromIso,
  scheduleToIso,
  type CampaignScheduleValue,
} from '../../components/CampaignScheduleFields';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import {
  CampaignNameEditor,
  campaignPageLabel,
} from '../../components/CampaignNameEditor';
import { t } from '../../i18n';

const PRESET_META: Record<string, { label: string; defaultName: string; blurb: string }> = {
  quantity_unlock_gift: {
    label: 'Add X Items, Get a Free Gift',
    defaultName: 'Add items, get a free gift',
    blurb: 'Reward customers based on item count in the cart.',
  },
  buy_x_get_y: {
    label: 'Buy X Get Y',
    defaultName: 'Buy X Get Y',
    blurb: 'Purchase product X, get product Y free or discounted.',
  },
  milestone_rewards: {
    label: 'Milestone Rewards',
    defaultName: 'Milestone rewards',
    blurb: 'Spend more to unlock multi-step rewards on a progress bar.',
  },
  spend_unlock_gift: {
    label: 'Spend X, Unlock a Free Gift',
    defaultName: 'Spend more, unlock a free gift',
    blurb: 'Customers who reach a defined cart value get a free gift.',
  },
};

/** Simpler editor for campaign types that do not yet have a dedicated wizard. */
export function GenericCampaignEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new' || id === 'configure';
  const api = useApi();
  const navigate = useNavigate();

  const preset = searchParams.get('preset') || '';
  const typeFromQuery = searchParams.get('type') || '';
  const { currency, moneyPrefix } = useStore();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('FREE_GIFT');
  const [priority, setPriority] = useState('0');
  const [minSubtotal, setMinSubtotal] = useState('50');
  const [minQuantity, setMinQuantity] = useState('3');
  const [buyQty, setBuyQty] = useState('2');
  const [getQty, setGetQty] = useState('1');
  const [autoAdd, setAutoAdd] = useState(true);
  const [schedule, setSchedule] = useState<CampaignScheduleValue>(defaultScheduleValue);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState(preset);
  const [loading, setLoading] = useState(!isNew);
  const embedded = isShopifyEmbedded();

  const formSnapshot = useMemo(
    () => ({
      name,
      description,
      type,
      priority,
      minSubtotal,
      minQuantity,
      buyQty,
      getQty,
      autoAdd,
      schedule,
      activePreset,
    }),
    [
      name,
      description,
      type,
      priority,
      minSubtotal,
      minQuantity,
      buyQty,
      getQty,
      autoAdd,
      schedule,
      activePreset,
    ],
  );
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(formSnapshot);

  useEffect(() => {
    if (!isNew) return;
    if (typeFromQuery) setType(typeFromQuery);
    if (preset) {
      setActivePreset(preset);
      const meta = PRESET_META[preset];
      if (meta) {
        setName((n) => n || meta.defaultName);
        setDescription((d) => d || meta.blurb);
      }
    }
    // Baseline after defaults for new campaign
    markClean(formSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, typeFromQuery, preset]);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api
      .get<{
        name: string;
        description?: string;
        type: string;
        priority: number;
        startsAt?: string | null;
        endsAt?: string | null;
        config?: Record<string, unknown>;
      }>(`/campaigns/${id}`)
      .then((c) => {
        setName(c.name);
        setDescription(c.description || '');
        setType(c.type);
        setPriority(String(c.priority ?? 0));
        setSchedule(scheduleFromIso(c.startsAt, c.endsAt));
        const fg = c.config?.freeGift as
          | { conditions?: { minSubtotal?: number; minQuantity?: number }; autoAdd?: boolean }
          | undefined;
        if (fg?.conditions?.minSubtotal) setMinSubtotal(String(fg.conditions.minSubtotal));
        if (fg?.conditions?.minQuantity) {
          setMinQuantity(String(fg.conditions.minQuantity));
          setActivePreset('quantity_unlock_gift');
        }
        if (fg?.autoAdd !== undefined) setAutoAdd(fg.autoAdd);
        const bx = c.config?.buyXGetY as { buyQuantity?: number; getQuantity?: number } | undefined;
        if (bx?.buyQuantity) setBuyQty(String(bx.buyQuantity));
        if (bx?.getQuantity) setGetQty(String(bx.getQuantity));
        if (c.type === 'BUY_X_GET_Y') setActivePreset('buy_x_get_y');
        if (c.type === 'CART_GOAL' || c.type === 'MILESTONE') setActivePreset('milestone_rewards');
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [api, id, isNew]);

  useEffect(() => {
    if (loading) return;
    if (!isNew) markClean(formSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  function buildConfig(): Record<string, unknown> {
    if (type === 'BUY_X_GET_Y' || activePreset === 'buy_x_get_y') {
      return {
        buyXGetY: {
          buyQuantity: Number(buyQty),
          getQuantity: Number(getQty),
          sameSku: false,
          mixAndMatch: true,
          autoAdd: false,
          selectionMode: 'manual',
          discountPercent: 100,
        },
      };
    }
    if (type === 'CART_GOAL' || type === 'MILESTONE' || activePreset === 'milestone_rewards') {
      const t = Number(minSubtotal);
      return {
        cartGoal: {
          milestones: [
            {
              id: 'm1',
              threshold: Math.max(1, Math.round(t * 0.5)),
              type: 'discount_percent',
              value: 5,
              label: '5% Off',
            },
            { id: 'm2', threshold: t, type: 'free_shipping', label: 'Free Shipping' },
            {
              id: 'm3',
              threshold: Math.round(t * 1.5),
              type: 'free_gift',
              label: 'Free Gift',
            },
          ],
          currency,
          showConfetti: true,
          animateProgress: true,
          progressBarStyle: 'linear',
        },
      };
    }
    if (activePreset === 'quantity_unlock_gift') {
      return {
        freeGift: {
          selectionMode: autoAdd ? 'auto' : 'manual',
          limitMode: 'one',
          autoAdd,
          allowReplacement: false,
          gifts: [],
          conditions: { minQuantity: Number(minQuantity) },
        },
      };
    }
    return {
      freeGift: {
        selectionMode: autoAdd ? 'auto' : 'manual',
        limitMode: 'one',
        autoAdd,
        allowReplacement: false,
        gifts: [],
        conditions: { minSubtotal: Number(minSubtotal) },
      },
    };
  }

  function validateBeforeSave(): string | null {
    if (!name.trim()) return 'Enter a campaign name before saving.';
    if (showQtyField && Number(minQuantity) < 1) {
      return 'Set a minimum item quantity greater than 0.';
    }
    if (showSpendField && Number(minSubtotal) <= 0) {
      return 'Set a cart spend threshold greater than 0.';
    }
    if (showBogo) {
      if (Number(buyQty) < 1) return 'Buy quantity must be at least 1.';
      if (Number(getQty) < 1) return 'Get quantity must be at least 1.';
    }
    return null;
  }

  async function save() {
    const invalid = validateBeforeSave();
    if (invalid) {
      setError(invalid);
      setMessage(null);
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const resolvedType =
        activePreset === 'buy_x_get_y'
          ? 'BUY_X_GET_Y'
          : activePreset === 'milestone_rewards'
            ? 'CART_GOAL'
            : type || 'FREE_GIFT';

      const { startsAt, endsAt } = scheduleToIso(schedule);
      const base = {
        name: name || PRESET_META[activePreset]?.defaultName || 'Untitled campaign',
        description,
        priority: Math.max(0, Math.floor(Number(priority) || 0)),
        startsAt: startsAt || null,
        endsAt: endsAt || null,
        config: buildConfig(),
        status: 'ACTIVE' as const,
      };

      if (isNew) {
        const created = await api.post<{ id: string }>('/campaigns', {
          ...base,
          type: resolvedType,
        });
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
        navigate(`/campaigns/${created.id}`);
      } else {
        await api.patch(`/campaigns/${id}`, { ...base, type: resolvedType });
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const b = getBaseline();
    if (!b) return;
    setName(b.name);
    setDescription(b.description);
    setType(b.type);
    setPriority(b.priority);
    setMinSubtotal(b.minSubtotal);
    setMinQuantity(b.minQuantity);
    setBuyQty(b.buyQty);
    setGetQty(b.getQty);
    setAutoAdd(b.autoAdd);
    setSchedule(b.schedule);
    setActivePreset(b.activePreset);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(b);
  }

  const presetLabel = PRESET_META[activePreset]?.label;
  const showQtyField = activePreset === 'quantity_unlock_gift';
  const showBogo = activePreset === 'buy_x_get_y' || type === 'BUY_X_GET_Y';
  const showSpendField =
    !showQtyField &&
    !showBogo &&
    (activePreset === 'milestone_rewards' ||
      type === 'CART_GOAL' ||
      type === 'MILESTONE' ||
      type === 'FREE_GIFT');

  const pageLabel = campaignPageLabel(
    !isNew,
    t(presetLabel || 'Campaign'),
  );

  return (
    <Page
      title={pageLabel}
      subtitle={name.trim() || undefined}
      backAction={{
        content: t("Campaigns"),
        onAction: () => navigate('/campaigns'),
      }}
      primaryAction={
        !embedded
          ? {
              content: t("Save"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(),
            }
          : undefined
      }
    >
      <ContextualSaveBar
        id="freebi-generic-campaign-save"
        open={embedded && dirty && !loading}
        saving={saving}
        onSave={save}
        onDiscard={discard}
      />
      <SavingOverlay open={saving} label={t("Saving campaign…")} />
      <BlockStack gap="400">
        <CampaignNameEditor
          name={name}
          onChange={setName}
          placeholder={
            PRESET_META[activePreset]?.defaultName || 'Campaign name'
          }
          defaultEditing={isNew && !name.trim()}
        />
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        <Layout>
          <Layout.Section>
            <Card>
              <FormLayout>
                {presetLabel && (
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" tone="subdued" variant="bodySm">
                      {t("Campaign type")}
                    </Text>
                    <Badge tone="info">{presetLabel}</Badge>
                  </InlineStack>
                )}
                <TextField
                  label={t("Description")}
                  value={description}
                  onChange={setDescription}
                  multiline={3}
                  autoComplete="off"
                />
                <TextField
                  label={t("Priority")}
                  type="number"
                  value={priority}
                  onChange={setPriority}
                  autoComplete="off"
                />
              </FormLayout>
            </Card>
            <div style={{ marginTop: 16 }}>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    {t("Advanced settings")}
                  </Text>
                  <CampaignScheduleFields value={schedule} onChange={setSchedule} />
                </BlockStack>
              </Card>
            </div>
            <div style={{ marginTop: 16 }}>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    {t("Offer settings")}
                  </Text>
                  {showSpendField && (
                    <TextField
                      label={`Minimum cart value (${currency})`}
                      type="number"
                      value={minSubtotal}
                      onChange={setMinSubtotal}
                      prefix={moneyPrefix}
                      autoComplete="off"
                    />
                  )}
                  {showQtyField && (
                    <TextField
                      label={t("Minimum items in cart")}
                      type="number"
                      value={minQuantity}
                      onChange={setMinQuantity}
                      autoComplete="off"
                    />
                  )}
                  {(showQtyField || type === 'FREE_GIFT') && !showBogo && (
                    <Checkbox
                      label={t("Automatically add gift to cart")}
                      checked={autoAdd}
                      onChange={setAutoAdd}
                    />
                  )}
                  {showBogo && (
                    <InlineStack gap="400">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={t("Buy quantity (X)")}
                          type="number"
                          value={buyQty}
                          onChange={setBuyQty}
                          autoComplete="off"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={t("Get quantity (Y)")}
                          type="number"
                          value={getQty}
                          onChange={setGetQty}
                          autoComplete="off"
                        />
                      </div>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            </div>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  {t("Free plan")}
                </Text>
                <Text as="p" tone="subdued">
                  {t("Unlimited campaigns.")}
                </Text>
                {isNew && (
                  <Button onClick={() => navigate('/campaigns/new')}>{t("Change campaign type")}</Button>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
}
